import { defaultDataLayer } from '../constants/content'

// GTM API requires dataLayer access through global window variable
declare global {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Window {
    // eslint-disable-next-line @typescript-eslint/ban-types
    dataLayer: object[]
    gtag: (...args: unknown[]) => void
  }
}

export enum EventAction {
  IntentSuccess = 'IntentSuccess',
  LeadSubmit = 'lead-submit',
}

export enum EventCategory {
  Signup = 'Signup',
  Lead = 'lead',
}

export enum EventLabel {
  FormFill = 'FormFill',
  Error = 'error',
  Success = 'success',
}

// We not decided yet about event payload format
export enum EventType {
  LegacyEvent = 'legacy.event',
  EnableAnalytics = 'event.enableAnalytics',
  GtmFormSubmit = 'gtm.formSubmit',
  FpVisitorIdSubmit = 'fp.visitorIdSubmit',
  SalesFormAttempt = 'sales_form_attempt',
  SalesFormPartiallyCompleted = 'sales_form_partialy_completed',
  SalesFormCompleted = 'sales_form_completed',
}

type SendEventProps = {
  action: EventAction
  category: EventCategory
  label?: EventLabel
}

function setupDataLayer() {
  // Required for development since without SSR sendEvent will be called before Helmet has a chance to inject the script that initializes dataLayer.
  window.dataLayer = window.dataLayer ?? defaultDataLayer
}

export function sendEvent(props: SendEventProps) {
  setupDataLayer()

  window.dataLayer.push({ event: EventType.LegacyEvent, eventProps: { ...props } })
}

export function enableAnalytics(visitorId: string) {
  setupDataLayer()

  window.dataLayer.push({ event: EventType.EnableAnalytics }, { enableAnalytics: true })
  trackVisitorId(visitorId)
}

export function trackEmbeddedFormSubmit() {
  sendEvent({ action: EventAction.IntentSuccess, category: EventCategory.Signup, label: EventLabel.FormFill })
}
export function trackVisitorId(visitorId: string) {
  window.dataLayer.push({ event: EventType.FpVisitorIdSubmit, 'fp.fpUserId': visitorId })
}

export function trackLeadSubmit(success = true) {
  sendEvent({
    action: EventAction.LeadSubmit,
    category: EventCategory.Lead,
    label: success ? EventLabel.Success : EventLabel.Error,
  })
}
export function sendFormEvent(elementClass: string) {
  setupDataLayer()

  window.dataLayer.push({ event: EventType.GtmFormSubmit, 'gtm.elementClasses': elementClass })
}

/**
 * Send sales form interaction events to the DataLayer
 * @param eventType The type of sales form event
 * @param additionalData Optional additional data to include with the event
 */
export async function sendSalesFormEvent(eventType: EventType, additionalData?: Record<string, unknown>) {
  setupDataLayer()

  // Import hashEmail function using dynamic import to avoid circular dependencies
  const { hashEmail } = await import('./hash')

  // Get email from additionalData if available
  const email = additionalData?.email as string | undefined

  // Add SHA256 hashed email for specific events
  const eventData: Record<string, unknown> = {
    event: eventType,
    ...additionalData,
  }

  // Add sha256_email parameter for specified events
  if ((eventType === EventType.SalesFormPartiallyCompleted || eventType === EventType.SalesFormCompleted) && email) {
    eventData.sha256_email = await hashEmail(email)
  }

  window.dataLayer.push(eventData)
}
